import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Truck, Fuel, Gauge, Calculator, TrendingUp, Lock, ArrowRight, ShieldCheck, Sparkles,
  Mail, CheckCircle2, ClipboardCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { applyPageSeo } from "@/lib/seo";
import { useToolGate } from "@/hooks/useToolGate";
import { AnonSignupLock, CommunityProNudge } from "@/components/ToolGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_URL = "https://routehaulpathways.com/cost-per-mile-calculator";
const OG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/ad103537-b988-4363-adb5-ef152c7e8f33/images/171fd1e04386e95f2b606f4efdd0db41c4b64411ac1b54b5e76955c5ad152ba2.jpeg";
const TITLE = "Free Cost-Per-Mile Calculator for Owner-Operators & Truckers | RouteHaul";
const DESCRIPTION =
  "Free trucking cost-per-mile (CPM) calculator for owner-operators. Enter your fixed and variable costs to find your true break-even rate per mile and the rate you should charge to hit your margin.";

const FREE_LIMIT = 2;
const STORE_KEY = "rh_cpm_free_uses_v1";

const perMile = (n) => `$${(isFinite(n) ? n : 0).toFixed(3)}`;
const money = (n) => `$${(isFinite(n) ? n : 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const num = (v) => parseFloat(v) || 0;

const Field = ({ id, label, value, onChange, prefix, suffix, step = "any" }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm text-slate-700">{label}</Label>
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>}
      <Input
        id={id}
        data-testid={`cpm-pub-${id}`}
        type="number"
        min="0"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`h-11 font-mono tabular ${prefix ? "pl-7" : ""} ${suffix ? "pr-14" : ""}`}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{suffix}</span>}
    </div>
  </div>
);

const Row = ({ label, value, highlight, muted, testid }) => (
  <div className="flex items-center justify-between">
    <span className={`text-sm ${muted ? "text-slate-400" : "text-slate-300"}`}>{label}</span>
    <span data-testid={testid} className={`font-mono tabular font-bold ${highlight ? "text-emerald-400 text-lg" : muted ? "text-slate-300" : "text-white"}`}>{value}</span>
  </div>
);

export default function CPMCalculatorPublic() {
  const navigate = useNavigate();
  const { user, isPro, isCommunity, isAnon, locked, anonFreeLeft, registerUse, ready } = useToolGate(STORE_KEY);

  const [results, setResults] = useState(null);
  const [f, setF] = useState({
    payment: 2200, insurance: 1200, permits: 300, otherFixed: 400,
    diesel: 4.0, mpg: 6.5, maintenance: 0.15, tires: 0.04, tollsMisc: 0.05,
    miles: 10000, margin: 20,
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  // SEO: title, meta description, canonical, OG image, JSON-LD
  useEffect(() => applyPageSeo({
    title: TITLE,
    description: DESCRIPTION,
    canonical: PAGE_URL,
    image: OG_IMAGE,
    imageAlt: "Free cost-per-mile calculator for owner-operators — RouteHaul Logistics Pathways",
    seoKey: "cpm",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Owner-Operator Cost-Per-Mile Calculator",
      description: DESCRIPTION,
      url: PAGE_URL,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      publisher: { "@type": "Organization", name: "RouteHaul Logistics Pathways", url: "https://routehaulpathways.com/" },
    },
  }), []);

  const compute = () => {
    const miles = num(f.miles);
    const fixedMonthly = num(f.payment) + num(f.insurance) + num(f.permits) + num(f.otherFixed);
    const fixedCPM = miles > 0 ? fixedMonthly / miles : 0;
    const fuelCPM = num(f.mpg) > 0 ? num(f.diesel) / num(f.mpg) : 0;
    const variableCPM = fuelCPM + num(f.maintenance) + num(f.tires) + num(f.tollsMisc);
    const totalCPM = fixedCPM + variableCPM;
    const suggestedRate = totalCPM * (1 + num(f.margin) / 100);
    const monthlyOperating = totalCPM * miles;
    return { miles, fixedMonthly, fixedCPM, fuelCPM, variableCPM, totalCPM, suggestedRate, monthlyOperating };
  };

  const handleCalculate = () => {
    if (locked) return;
    setResults(compute());
    registerUse();
  };

  const loadBoardTo = isPro ? "/loadboard" : isCommunity ? "/subscribe" : "/register";

  // Optional lead capture (stored for admin; no customer email is sent yet).
  const [leadEmail, setLeadEmail] = useState("");
  const [leadStatus, setLeadStatus] = useState("idle"); // idle | saving | done
  const submitLead = async () => {
    const email = leadEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLeadStatus("saving");
    try {
      await axios.post(`${API}/cpm/lead`, {
        email,
        total_cpm: results?.totalCPM,
        suggested_rate: results?.suggestedRate,
        miles: results?.miles,
        margin: num(f.margin),
      });
      setLeadStatus("done");
      toast.success("Thanks! Your numbers are saved — LaRaine will be in touch.");
    } catch {
      setLeadStatus("idle");
      toast.error("Couldn't save that just now. Please try again.");
    }
  };

  const fixedPct = results && results.totalCPM > 0 ? (results.fixedCPM / results.totalCPM) * 100 : 0;

  // "Good load / avoid" cheat rows derived from the user's break-even + target rate.
  const rateRows = results ? [
    { label: "Avoid — below your cost", range: `Under ${perMile(results.totalCPM)}`, cls: "text-red-600", dot: "bg-red-500" },
    { label: "Fair — covers cost, thin profit", range: `${perMile(results.totalCPM)}–${perMile(results.suggestedRate)}`, cls: "text-amber-600", dot: "bg-amber-400" },
    { label: `Good — hits your ${num(f.margin)}% margin`, range: `${perMile(results.suggestedRate)}+`, cls: "text-emerald-600", dot: "bg-emerald-500" },
    { label: "Strong — book it fast", range: `${perMile(results.suggestedRate * 1.2)}+`, cls: "text-emerald-700", dot: "bg-emerald-600" },
  ] : [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cpm-public-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="cpm-brand" className="flex items-center gap-2 font-heading font-black text-lg tracking-tight text-slate-900">
            <Truck className="w-6 h-6 text-brand" strokeWidth={2.5} />
            <span>RouteHaul<span className="hidden sm:inline text-slate-400 font-bold"> Logistics Pathways</span></span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <Link to="/" data-testid="cpm-dashboard"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Go to dashboard</Button></Link>
            ) : (
              <>
                <Link to="/login" data-testid="cpm-login"><Button variant="ghost" className="h-10 px-4 rounded-full text-slate-700 font-bold text-xs sm:text-sm tracking-wide uppercase hover:bg-slate-100">Log in</Button></Link>
                <Link to="/register" data-testid="cpm-header-signup"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-14 pb-4">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">
            <Calculator className="w-4 h-4" /> Free owner-operator tool
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-black text-slate-900 leading-[1.02]">
            Free Cost-Per-Mile Calculator
          </h1>
          <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
            Know your true cost per mile before you accept a load. Enter your fixed and variable costs,
            then hit calculate to see your break-even rate and the rate you should charge to hit your margin.
          </p>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24 grid lg:grid-cols-5 gap-8 items-start">
        {/* Inputs */}
        <div className="lg:col-span-3 space-y-8">
          <div className="border border-slate-200 rounded-md p-6 bg-white">
            <div className="flex items-center gap-2 mb-5">
              <Truck className="w-4 h-4 text-brand" />
              <h2 className="font-heading text-lg font-semibold text-slate-900">Fixed monthly costs</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="payment" label="Truck / trailer payment" value={f.payment} onChange={set("payment")} prefix="$" />
              <Field id="insurance" label="Insurance" value={f.insurance} onChange={set("insurance")} prefix="$" />
              <Field id="permits" label="Permits & compliance (IFTA, UCR, ELD)" value={f.permits} onChange={set("permits")} prefix="$" />
              <Field id="otherFixed" label="Parking / other fixed" value={f.otherFixed} onChange={set("otherFixed")} prefix="$" />
            </div>
          </div>

          <div className="border border-slate-200 rounded-md p-6 bg-white">
            <div className="flex items-center gap-2 mb-5">
              <Fuel className="w-4 h-4 text-brand" />
              <h2 className="font-heading text-lg font-semibold text-slate-900">Variable costs</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="diesel" label="Diesel price" value={f.diesel} onChange={set("diesel")} prefix="$" suffix="/gal" />
              <Field id="mpg" label="Truck fuel economy" value={f.mpg} onChange={set("mpg")} suffix="MPG" />
              <Field id="maintenance" label="Maintenance" value={f.maintenance} onChange={set("maintenance")} prefix="$" suffix="/mi" />
              <Field id="tires" label="Tires" value={f.tires} onChange={set("tires")} prefix="$" suffix="/mi" />
              <Field id="tollsMisc" label="Tolls & misc" value={f.tollsMisc} onChange={set("tollsMisc")} prefix="$" suffix="/mi" />
            </div>
          </div>

          <div className="border border-slate-200 rounded-md p-6 bg-white">
            <div className="flex items-center gap-2 mb-5">
              <Gauge className="w-4 h-4 text-brand" />
              <h2 className="font-heading text-lg font-semibold text-slate-900">Operation</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="miles" label="Miles driven per month" value={f.miles} onChange={set("miles")} suffix="mi" />
              <Field id="margin" label="Target profit margin" value={f.margin} onChange={set("margin")} suffix="%" />
            </div>
          </div>

          {/* Calculate / gate */}
          {!locked ? (
            <div>
              <Button
                onClick={handleCalculate}
                data-testid="cpm-calculate-btn"
                className="h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white text-base font-bold w-full sm:w-auto group"
              >
                <Calculator className="w-5 h-5 mr-2" />
                Calculate my cost per mile
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              {ready && isAnon && anonFreeLeft > 0 && (
                <p className="mt-3 text-sm text-slate-500" data-testid="cpm-free-left">
                  1 free calculation — create a free account for unlimited use
                </p>
              )}
              {isCommunity && results && (
                <div className="mt-6"><CommunityProNudge testid="cpm-pro-nudge" perk="unlock peer benchmarks for your equipment" /></div>
              )}
            </div>
          ) : (
            <AnonSignupLock testid="cpm-anon-lock" perks={[
              "Unlimited cost-per-mile calculations",
              "The full 12-Step Business Foundation Guide",
              "Searchable trucking glossary & readiness diagnostic",
            ]} />
          )}
        </div>

        {/* Results */}
        <div className="lg:col-span-2 lg:sticky lg:top-24">
          {!results ? (
            <div className="border border-dashed border-slate-300 rounded-md p-10 text-center bg-white" data-testid="cpm-empty">
              <Calculator className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Enter your numbers and hit <span className="font-bold text-slate-700">Calculate</span> to see your true cost per mile.</p>
            </div>
          ) : (
            <>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-900 text-white rounded-md p-8">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Your true cost per mile</div>
                <div className="font-mono font-bold tabular text-brand leading-none" data-testid="cpm-pub-total" style={{ fontSize: "clamp(3.5rem, 8vw, 5rem)" }}>
                  {perMile(results.totalCPM)}
                </div>
                <div className="text-slate-500 text-sm mt-1">break-even rate per mile</div>

                <div className="mt-8 space-y-3">
                  <Row label="Fixed cost / mile" value={perMile(results.fixedCPM)} />
                  <Row label="Fuel cost / mile" value={perMile(results.fuelCPM)} />
                  <Row label="Variable cost / mile" value={perMile(results.variableCPM)} />
                  <div className="h-px bg-white/10 my-2" />
                  <Row label={`Suggested rate (+${num(f.margin)}% margin)`} value={perMile(results.suggestedRate)} highlight testid="cpm-pub-suggested" />
                </div>

                <div className="mt-8">
                  <div className="flex h-2.5 rounded-full overflow-hidden bg-white/10">
                    <div className="bg-brand" style={{ width: `${fixedPct}%` }} />
                    <div className="bg-emerald-400" style={{ width: `${100 - fixedPct}%` }} />
                  </div>
                  <div className="flex justify-between text-[11px] text-slate-400 mt-2">
                    <span><span className="inline-block w-2 h-2 rounded-full bg-brand mr-1" />Fixed {Math.round(fixedPct)}%</span>
                    <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-400 mr-1" />Variable {Math.round(100 - fixedPct)}%</span>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-white/10 space-y-3">
                  <Row label="Fixed costs / month" value={money(results.fixedMonthly)} muted />
                  <Row label="Total operating cost / month" value={money(results.monthlyOperating)} muted />
                </div>

                <div className="mt-6 flex items-start gap-2 text-xs text-slate-400 leading-relaxed">
                  <TrendingUp className="w-4 h-4 text-brand shrink-0 mt-0.5" />
                  Any load paying under {perMile(results.totalCPM)}/mile loses you money. Aim for {perMile(results.suggestedRate)}+ to hit your margin.
                </div>

                {/* Pro-only benchmark teaser */}
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">
                    <Lock className="w-3.5 h-3.5" /> Pro: peer benchmarks
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    See how your cost per mile stacks up against anonymized owner-operators running your
                    equipment — a Pro feature.
                  </p>
                </div>
              </motion.div>

              {/* Rate cheat table — teases the Pro Load Board */}
              <div className="mt-4 border border-slate-200 rounded-md bg-white overflow-hidden" data-testid="cpm-rate-table">
                <div className="px-5 pt-5 pb-3">
                  <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand mb-1">
                    <ClipboardCheck className="w-4 h-4" /> Good load / avoid
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Based on your numbers, here's how to read a load's rate per mile before you book:
                  </p>
                </div>
                <div className="divide-y divide-slate-100">
                  {rateRows.map((r, i) => (
                    <div key={i} className="flex items-center justify-between px-5 py-2.5" data-testid={`cpm-rate-row-${i}`}>
                      <span className="flex items-center gap-2 text-sm text-slate-700">
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${r.dot}`} />
                        {r.label}
                      </span>
                      <span className={`font-mono tabular text-sm font-bold ${r.cls}`}>{r.range}</span>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-4 bg-slate-50 border-t border-slate-100">
                  <p className="text-xs text-slate-500 leading-relaxed mb-3">
                    On the Pro <span className="font-bold text-slate-700">Simulated Load Board</span>, every load is auto-scored
                    good / fair / avoid against numbers like these — so you practice spotting winners before real money's on the line.
                  </p>
                  <Button
                    onClick={() => navigate(loadBoardTo)}
                    data-testid="cpm-loadboard-cta"
                    variant="outline"
                    className="h-10 px-5 rounded-full border-brand text-brand hover:bg-orange-50 text-sm font-bold w-full sm:w-auto"
                  >
                    {isPro ? "Open the Load Board" : "See it on the Pro Load Board"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>

              {/* Optional lead capture */}
              <div className="mt-4 border border-slate-200 rounded-md bg-white p-5" data-testid="cpm-lead-box">
                {leadStatus === "done" ? (
                  <div className="flex items-start gap-3" data-testid="cpm-lead-success">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-heading font-bold text-slate-900 text-sm">You're all set.</p>
                      <p className="text-sm text-slate-600 mt-0.5">We saved your numbers — LaRaine will reach out with lanes that clear your cost per mile.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Mail className="w-4 h-4 text-brand" />
                      <h3 className="font-heading text-base font-bold text-slate-900">Get a free lane review</h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed mb-3">
                      Optional — drop your email and our lane strategist will follow up with lanes that beat your break-even.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        type="email"
                        inputMode="email"
                        placeholder="you@example.com"
                        value={leadEmail}
                        onChange={(e) => setLeadEmail(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") submitLead(); }}
                        data-testid="cpm-lead-email"
                        className="h-11 flex-1"
                      />
                      <Button
                        onClick={submitLead}
                        disabled={leadStatus === "saving"}
                        data-testid="cpm-lead-submit"
                        className="h-11 px-6 rounded-full bg-brand hover:bg-brand-hover text-white text-sm font-bold shrink-0"
                      >
                        {leadStatus === "saving" ? "Saving…" : "Get my free lane review"}
                      </Button>
                    </div>
                  </>
                )}
              </div>

              <p className="text-xs text-slate-400 mt-4 text-center">Estimates only — not financial advice.</p>
            </>
          )}
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-brand text-white">
        <div className="max-w-3xl mx-auto px-6 py-14 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black leading-tight">Turn your numbers into profitable lanes</h2>
          <p className="mt-4 text-orange-50 text-lg">Create a free account for the 12-Step Business Foundation Guide, then go Pro for unlimited calculations, peer benchmarks and the full toolkit.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="cpm-band-signup">
              <Button className="h-14 px-8 rounded-full bg-white text-brand hover:bg-orange-50 text-base font-bold group w-full sm:w-auto">
                Start the free guide
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/owner-operator-faq" data-testid="cpm-band-faq">
              <Button variant="outline" className="h-14 px-8 rounded-full border-white/60 bg-transparent text-white hover:bg-white/10 text-base font-bold w-full sm:w-auto">
                Owner-operator FAQ
              </Button>
            </Link>
          </div>
          <p className="mt-6 inline-flex items-center gap-2 text-sm text-orange-50/90">
            <ShieldCheck className="w-4 h-4" /> Educational mentorship — not legal, tax or financial advice.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} RouteHaul Logistics Pathways</span>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <Link to="/how-to-get-your-own-authority" className="hover:text-slate-900">Get Your Authority</Link>
            <Link to="/how-much-do-owner-operators-make" className="hover:text-slate-900">Owner-Operator Pay</Link>
            <Link to="/ifta-fuel-tax-calculator" className="hover:text-slate-900">IFTA Calculator</Link>
            <Link to="/trucking-glossary" className="hover:text-slate-900">Glossary</Link>
            <Link to="/owner-operator-faq" className="hover:text-slate-900">FAQ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
